import React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * Italic accent word inside a page title, matching the dashboard mockup's
 * ``.page-title em`` treatment (serif italic in the accent color).
 */
export const Accent: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="em" sx={{ fontStyle: 'italic', color: 'primary.light' }}>
    {children}
  </Box>
);

interface PageHeaderProps {
  /** Title node — compose with <Accent> to highlight a word. */
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Right-aligned controls (buttons, status chips, etc.). */
  actions?: React.ReactNode;
}

/**
 * Shared page heading used by the primary tabs: a serif display title with an
 * optional accent word, an optional subtitle, and right-aligned actions.
 */
const PageHeader: React.FC<PageHeaderProps> = ({ title, subtitle, actions }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: subtitle ? 'flex-start' : 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: 1.5,
      pt: 1,
      pb: 2.5,
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="h2">{title}</Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: '72ch' }}>
          {subtitle}
        </Typography>
      )}
    </Box>
    {actions && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>{actions}</Box>
    )}
  </Box>
);

export default PageHeader;
